import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-16 flex items-center justify-center">
        <Card className="glass max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === 'verifying' && <Loader2 className="w-16 h-16 text-rose-500 animate-spin" />}
              {status === 'success' && <CheckCircle className="w-16 h-16 text-green-500" />}
              {status === 'error' && <XCircle className="w-16 h-16 text-red-500" />}
            </div>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
              {status === 'verifying' && 'Verifying Your Email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription>
              {message || 'Please wait while we verify your email address...'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'success' && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-800 mb-2">Your email has been successfully verified!</p>
                  <p className="text-sm text-green-700">You can now sign in and access all platform features.</p>
                </div>
                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full bg-rose-600 hover:bg-rose-700"
                >
                  Sign In Now
                </Button>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-red-800 mb-2">{message}</p>
                  <p className="text-sm text-red-700">The verification link may have expired or is invalid.</p>
                </div>
                <div className="space-y-2">
                  <Button 
                    onClick={() => navigate('/?resend=true')} 
                    className="w-full bg-rose-600 hover:bg-rose-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Request New Verification Email
                  </Button>
                  <Button 
                    onClick={() => navigate('/')} 
                    variant="outline"
                    className="w-full"
                  >
                    Back to Home
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
